import React from 'react'
import { useTranslation } from 'react-i18next'
import { getFeature, deduplicateFeatures } from './featureCatalog'
import './PlanCard.css'

/**
 * Individual plan card component
 */
const PlanCard = ({ plan, billingPeriod = 'monthly', isCurrentPlan, onSelect, highlighted, disabled, hasActiveSubscription }) => {
  const { t } = useTranslation('subscription')

  const handleSelect = () => {
    if (isCurrentPlan || disabled) return
    onSelect(plan.id)
  }

  // Determine if this plan is "popular" (only show if user has no active subscription)
  const isPopular = !hasActiveSubscription && (plan.is_popular || plan.id === 'starter')

  // Calculate displayed price based on billing period (with safe fallbacks)
  const displayPrice = billingPeriod === 'yearly'
    ? (plan.price_annual ? (plan.price_annual / 12).toFixed(2) : (plan.price_monthly || '0.00'))
    : (plan.price_monthly || '0.00')

  // Calculate savings percentage for yearly billing (with safe fallbacks)
  const savingsPercentage = plan.price_annual && plan.price_monthly && plan.price_monthly > 0
    ? Math.round((1 - (plan.price_annual / 12) / plan.price_monthly) * 100)
    : 0

  // Ensure features is an array
  const features = Array.isArray(plan.features) ? plan.features : []

  // Check if this is the free plan
  const isFree = plan.id === 'free'

  // Translation helper for plan details
  const getTranslatedPlanDetail = (field) => {
    const key = `planCard.plans.${plan.id}.${field}`
    const translated = t(key, { defaultValue: null })
    return translated !== key ? translated : plan[field]
  }

  // Get translated feature name from feature ID
  const getTranslatedFeature = (featureId) => {
    const feature = getFeature(featureId)
    const translationKey = feature.translationKey
    const translated = t(translationKey, { defaultValue: null })
    return translated && translated !== translationKey ? translated : feature.name
  }

  // Filter features for display (exclude base languages, analytics, and individual language addons)
  const getDisplayFeatures = (featureIds) => {
    const featureIdArray = Array.isArray(featureIds) ? featureIds : []

    // Check if plan has European languages (any lang_ feature except de/en)
    const europeanLangIds = ['lang_fr', 'lang_es', 'lang_it', 'lang_nl', 'lang_pt', 'lang_pl',
      'lang_sv', 'lang_no', 'lang_da', 'lang_fi', 'lang_el', 'lang_cs', 'lang_hu', 'lang_ro',
      'lang_bg', 'lang_hr', 'lang_sk', 'lang_sl', 'lang_lt', 'lang_lv', 'lang_et', 'lang_tr']
    const hasEuropeanLanguages = europeanLangIds.some(id => featureIdArray.includes(id))

    const filtered = featureIdArray.filter(featureId => {
      const feature = getFeature(featureId)
      // Exclude internal/base features AND individual language addons
      const hideCategories = ['base_language', 'analytics', 'language_addon']
      return !hideCategories.includes(feature.category)
    })

    // Deduplicate features with same name (e.g., emoji_control and hashtag_control)
    const deduplicated = deduplicateFeatures(filtered)

    // Add "All European Languages" as synthetic feature if plan has them
    if (hasEuropeanLanguages) {
      return ['all_european_languages', ...deduplicated]
    }

    return deduplicated
  }

  return (
    <div className={`plan-card ${isCurrentPlan ? 'current' : ''} ${isPopular ? 'popular' : ''} ${isFree ? 'free' : ''}`}>
      {/* Popular Badge */}
      {isPopular && !isCurrentPlan && (
        <div className="popular-badge">
          {t('planCard.badges.mostPopular')}
        </div>
      )}

      {/* Current Plan Badge */}
      {isCurrentPlan && (
        <div className="current-badge">
          {t('planCard.badges.currentPlan')}
        </div>
      )}

      {/* Plan Header */}
      <div className="plan-header">
        <h3 className="plan-name">{getTranslatedPlanDetail('name')}</h3>
      </div>

      {/* Pricing */}
      <div className="plan-pricing">
        <div className="price-container">
          <span className="currency">{t('planCard.pricing.currency')}</span>
          <span className="amount">{displayPrice}</span>
          <span className="period">{t('planCard.pricing.perMonth')}</span>
        </div>
        {billingPeriod === 'yearly' && plan.price_annual && savingsPercentage > 0 && (
          <p className="billing-info">
            {t('planCard.pricing.yearlyPrice', { price: plan.price_annual, savings: savingsPercentage })}
          </p>
        )}
        {billingPeriod === 'monthly' && plan.price_annual && (
          <p className="billing-info">
            {t('planCard.pricing.orYearly', { price: plan.price_annual })}
          </p>
        )}
        {!isFree && <p className="vat-notice">{t('planCard.pricing.vat')}</p>}
        {isFree && <p className="vat-notice free-notice">{t('planCard.pricing.freeForever')}</p>}
      </div>

      {/* Limits Hero Box - Main value proposition */}
      <div className={`limits-hero ${isPopular ? 'popular' : ''} ${isFree ? 'free' : ''}`}>
        <div className="limits-headline">{getTranslatedPlanDetail('headline')}</div>
        <div className="limits-subline">{getTranslatedPlanDetail('subline')}</div>
      </div>

      {/* Short Description */}
      {plan.description && (
        <p className="plan-description">{getTranslatedPlanDetail('description')}</p>
      )}

      {/* Select Button */}
      <div className="plan-footer">
        <button
          className={`select-button ${isCurrentPlan ? 'current' : ''} ${isPopular ? 'popular' : ''} ${isFree ? 'free' : ''}`}
          onClick={handleSelect}
          disabled={isCurrentPlan || disabled}
        >
          {isCurrentPlan ? t('planCard.buttons.currentPlan') : disabled ? t('planCard.buttons.loading') : t('planCard.buttons.choosePlan')}
        </button>
      </div>
    </div>
  )
}

export default PlanCard
